import { useMutation, useQuery } from '@apollo/client'
import { NetworkStatus } from 'apollo-boost'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRoutesContext } from '../components/RoutesContext'
import { UserRoles } from '../constants/user'
import { CreateCitationInput } from '../globalTypes'
import { CREATE_BULK_CITATIONS } from '../queries/Citations'
import {
  ConvertCitationFormat,
  ConvertCitationFormatVariables,
} from '../queries/ConvertCitationFormat'
import {
  CreateBulkCitations,
  CreateBulkCitationsVariables,
} from '../queries/CreateBulkCitations'
import { CONVERT_CITATION_FORMAT } from '../queries/ImportCitations'
import { getCreateCitationData } from '../utils/citations'
import { navigated } from '../state/app/actions'
import { createProject } from '../state/currentProject/actions'
import { initializedSelector } from '../state/app/selectors'
import {
  currentProjectIdSelector,
  currentProjectSetInProgressSelector,
} from '../state/currentProject/selectors'
import useUserRole from './useUserRole'

export const useConvertAndImport = (rawCitationData, isPremiumFlow: boolean = false) => {
  const userRole = useUserRole()
  const routes = useRoutesContext()

  const dispatch = useDispatch()

  const projectId = useSelector(currentProjectIdSelector)
  const initialized = useSelector(initializedSelector)
  const currentProjectSettingUp = useSelector(currentProjectSetInProgressSelector)

  const [clicked, setClicked] = useState<boolean>(false)
  const [withAnnotation, setWithAnnotation] = useState<boolean | null>(null)

  // On click callback to create project if not created.
  const citationCreator = useCallback(() => {
    if (!projectId) {
      dispatch(createProject())
    }
    if (withAnnotation == null) setWithAnnotation(false)
    setClicked(true)
  }, [dispatch, projectId, withAnnotation])

  // Convert citation format
  const { error: convertError, data, networkStatus: convertNetworkStatus } = useQuery<
    ConvertCitationFormat,
    ConvertCitationFormatVariables
  >(CONVERT_CITATION_FORMAT, {
    variables: {
      records: rawCitationData ?? '',
    },
    skip: !initialized,
  })

  // Mutation to bulk create citations
  const [createBulkCitations, { error: mutationError }] = useMutation<
    CreateBulkCitations,
    CreateBulkCitationsVariables
  >(CREATE_BULK_CITATIONS, {
    onCompleted: () => {
      if (userRole === UserRoles.PRO_USER && isPremiumFlow) {
        dispatch(navigated(routes.toCitationList({ projectId }), { replace: true }))
      } else dispatch(navigated(routes.toProject(), { replace: true }))
    },
  })

  useEffect(() => {
    if (data && projectId && data.convertCitationFormat.length > 0 && clicked) {
      const inputCitationsData: CreateCitationInput[] = data.convertCitationFormat.map(
        (citation) => {
          return getCreateCitationData(citation, withAnnotation)
        }
      )
      createBulkCitations({ variables: { projectId, dataArray: inputCitationsData } })
    }
  }, [projectId, data, createBulkCitations, withAnnotation, clicked])

  let isLoading = false
  if (
    convertNetworkStatus !== NetworkStatus.ready ||
    !data ||
    !initialized ||
    currentProjectSettingUp ||
    clicked
  ) {
    isLoading = true
  }
  return {
    convertData: data,
    setWithAnnotation,
    mutationError,
    convertError,
    citationCreator,
    isLoading,
  }
}

export default useConvertAndImport
